import React from 'react';
import { isMobile } from 'utils/platform';
import Box from 'ui/Box';
import styled from 'styled-components';
import media from 'ui/media';
import { useIntl } from 'react-intl';
import { ThemeFontStyleTypeEnum } from '@inter-ikea-kompis/enums/lib';
import Body from 'ui/Typography/Body';
import convertHexToRgba from 'utils/convertHexToRgba';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { ComponentIds } from '@inter-ikea-core/core';
import { useCore } from 'contexts/core';

type Props = {
  message: string;
  image: string;
};

const Wrapper = styled(Box)`
  padding-top: ${({ theme }) => theme.space.xxl};
  padding-left: ${({ theme }) => theme.space.xxl};
  padding-right: ${({ theme }) => theme.space.xxl};
  position: relative;
  min-height: 360px;
  ${media.tablet`
    padding: 1.25rem;
    min-height: 0;
  `}
`;

const MessageWrapper = styled(Box)`
  position: absolute;
  bottom: 50%;
  left: 50%;
  margin-left: -8rem;
`;

const MessageBubble = styled.div`
  position: relative;
  width: 16em;
  border: 1px solid ${({ theme }) => theme.colors.grey[200]};
  background-color: #fff;
  padding: 1.125em 1.5em;
  box-shadow: 20px 20px 84px rgba(0, 0, 0, 0.1017);

  &::after {
    content: '';
    position: absolute;
    width: 0;
    height: 0;
    top: 100%;
    left: 50%;
    margin-left: -0.75rem;
    border: 0.75rem solid transparent;
    border-bottom: none;
    border-top-color: #fff;
    filter: drop-shadow(0 0.07rem 0 rgba(0, 0, 0, 0.2));
  }
`;

const IndicatorImage = styled.div`
  left: 50%;
  position: absolute;
  margin-left: -1rem;
  margin-top: 1rem;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 3px solid transparent;
  width: ${({ theme }) => theme.space.xl};
  height: ${({ theme }) => theme.space.xl};
  background: ${({ theme }) => convertHexToRgba(theme.colors.indicator.default, 0.8)};
  box-shadow: ${({ theme }) => theme.boxShadow.elevated};
  outline-offset: 4px;
  transition: border-color 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);

  &::after {
    content: '';
    border-radius: 50%;
    border: 6px solid transparent;
    background: ${({ theme }) => theme.colors.white};
    z-index: 2;
    transition: transform 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  }
`;

const Image = styled.img`
  max-width: 100%;
`;

export default function SelectInSceneInfo({ message, image }: Props) {
  const { formatMessage } = useIntl();
  const testId = isMobile()
    ? 'select-in-scene-mobile-container'
    : 'select-in-scene-desktop-container';

  const coreApi = useCore();
  const framesInScene = coreApi.entities.getByComponent(ComponentIds.FRAME_COMPONENT_ID);

  // If mobile and no frames in scene we want to override with no frames in scene message
  if (isMobile() && framesInScene.length === 0) {
    return (
      <Wrapper pt={0} paddingX={'l'} data-testid={testId}>
        <SkapaInlineMessage
          body={formatMessage({ id: 'storageone.errorMessageNoFramesInSceneBody' })}
          variant={InlineMessageVariantEnum.NEGATIVE}
          subtle
        />
      </Wrapper>
    );
  }

  return (
    <Wrapper pt={isMobile() ? '0' : 'xxl'} paddingX={isMobile() ? 'l' : '0'} data-testid={testId}>
      {!isMobile() ? (
        <>
          <MessageWrapper>
            <MessageBubble>
              <Body type={ThemeFontStyleTypeEnum.bodyMedium}>{formatMessage({ id: message })}</Body>
            </MessageBubble>
            <IndicatorImage />
          </MessageWrapper>

          <Box pt="l">
            <Image src={image} alt={formatMessage({ id: message })} />
          </Box>
        </>
      ) : (
        <SkapaInlineMessage
          body={formatMessage({ id: message })}
          variant={InlineMessageVariantEnum.INFORMATIVE}
          ssrIcon={InformationCircle}
          subtle
        />
      )}
    </Wrapper>
  );
}
